// SelectRequestView – modal to pick a request and filter the device list by it.

import React, { JSX, useEffect, useMemo, useState } from 'react';
import { formatApiResponseError } from '../../EventsManagementView/utils';
import DeviceIdTableFilterBar from '../../shared/DeviceIdTableFilterBar';
import CyclingLoader from '../../shared/CyclingLoader';
import { PortaledLayer } from '../../shared/PortaledLayer';

// ---- Types ----

type RequestRow = Record<string, unknown>;

type SelectRequestDialogProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (requestId: string) => void;
  operatorScopedEventId?: string | null;
};

// ---- Helpers ----

function parseBodyJson(row: Record<string, unknown>): Record<string, unknown> {
  try {
    const bodyJson = row?.bodyJson;
    if (typeof bodyJson === 'string') return JSON.parse(bodyJson) as Record<string, unknown>;
    if (typeof bodyJson === 'object' && bodyJson) return bodyJson as Record<string, unknown>;
  } catch {
    // ignore
  }
  return {};
}

function normalizeRequestRow(row: Record<string, unknown>): RequestRow {
  const body = parseBodyJson(row as Record<string, unknown>);
  const username = row?.userUsername;


  return {
    ...row,
    requestId: (row?.requestId ?? row?.id ?? body?.requestId ?? '') as string,
    createdAt: (row?.createdAt ?? body?.createdAt ?? 0) as number,
    type: (body?.type ?? row?.type ?? '') as string,
    username,
    deviceIds: (body?.deviceIds ?? row?.deviceIds ?? []) as unknown[],
  };
}

function fmtTs(x: unknown): string {
  const n = typeof x === 'number' ? x : Number(x || 0);
  if (!n) return '—';
  const ms = n > 1e12 ? n : n * 1000;
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return String(x);
  }
}

// ---- Component ----

export default function SelectRequestDialog(props: SelectRequestDialogProps): JSX.Element | null {
  const { open, onClose, onSelect, operatorScopedEventId = null } = props;
  const scopedEventId = String(operatorScopedEventId ?? '').trim();

  const [rows, setRows] = useState<RequestRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Load all requests when the modal opens.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const res = scopedEventId
          ? await window.eventsAPI.listEventRequests(scopedEventId)
          : await window.eventsAPI.listRequests();
        if (cancelled) return;

        if (!res?.ok) {
          throw new Error(formatApiResponseError(res, 'Failed to load requests'));
        }

        const data = (res?.data ?? {}) as { rows?: unknown[] };
        const arr = Array.isArray(data.rows) ? data.rows : [];
        const normalized = (arr as Record<string, unknown>[]).map(normalizeRequestRow);

        setRows(normalized);
      } catch (err: unknown) {
        if (cancelled) return;
        setError((err as Error)?.message ?? 'Failed to load requests');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [open, scopedEventId]);

  // Unique request types for the type dropdown.
  const allTypes = useMemo(() => {
    return Array.from(
      new Set(rows.map((r) => String(r.type ?? '').trim()).filter(Boolean))
    ).sort();
  }, [rows]);

  // Apply search + type filter and sort by created desc.
  const filtered = useMemo(() => {
    let out = rows;

    if (typeFilter.trim()) {
      const needle = typeFilter.trim().toLowerCase();
      out = out.filter((r) =>
        String(r.type ?? '').toLowerCase().includes(needle)
      );
    }

    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter((r) => {
        if (String(r.requestId ?? '').toLowerCase().includes(needle)) return true;
        const ids = r.deviceIds;
        if (!Array.isArray(ids)) return false;
        return ids.some((id) => String(id).toLowerCase().includes(needle));
      });
    }

    out = [...out].sort(
      (a, b) => Number(b.createdAt ?? 0) - Number(a.createdAt ?? 0)
    );
    return out;
  }, [rows, q, typeFilter]);

  if (!open) return null;

  return (
    <PortaledLayer>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/70">
        <div className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
        {/* Header – title and close */}
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 p-4 dark:border-slate-700 dark:bg-slate-800">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">Select Request Filter</div>
            <div className="text-xs text-gray-600 dark:text-slate-400 truncate">
              Pick a request to filter the Devices table
            </div>
          </div>

          <button
            type="button"
            className="rounded border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        {/* Toolbar – search, type filter, total count */}
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
          <DeviceIdTableFilterBar
            value={q}
            onChange={setQ}
            inputClassName="px-2 py-1 text-xs border border-gray-200 dark:border-slate-700 rounded w-64 max-w-full h-8 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />

          <div className="text-xs flex items-center gap-2">
            <span className="text-gray-600 dark:text-slate-400">Type:</span>
            <select
              className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All</option>
              {allTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="ml-auto text-xs text-gray-700 dark:text-slate-300">
            Total: {filtered.length}
          </div>
        </div>

        {/* Request list – loading / error / empty / table */}
        <div className="overflow-auto bg-white p-4 dark:bg-slate-900">
          {loading ? (
            <div className="flex justify-center py-8">
              <CyclingLoader phrases={['Loading requests…']} size="sm" />
            </div>
          ) : null}
          {error && <div className="text-sm text-red-600">Error: {error}</div>}

          {!loading && !error && filtered.length === 0 && (
            <div className="text-sm text-gray-500 dark:text-slate-400">No requests found.</div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <table className="w-full border border-gray-200 text-xs dark:border-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-800">
                <tr>
                  <th className="border border-gray-200 p-2 text-left dark:border-slate-700">Request</th>
                  <th className="border border-gray-200 p-2 text-left dark:border-slate-700">Type</th>
                  <th className="border border-gray-200 p-2 text-left dark:border-slate-700">Username</th>
                  <th className="border border-gray-200 p-2 text-left dark:border-slate-700">Created</th>
                  <th className="border border-gray-200 p-2 text-right dark:border-slate-700">Devices</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const rid = String(r.requestId ?? '');
                  const deviceIds = r.deviceIds as unknown[];
                  const deviceCount = Array.isArray(deviceIds) ? deviceIds.length : 0;

                  return (
                    <tr
                      key={rid}
                      className="hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer"
                      onClick={() => {
                        if (!rid) return;
                        onSelect(rid);
                      }}
                    >
                      <td className="break-all border border-gray-200 p-2 font-mono dark:border-slate-700">
                        {rid || '—'}
                      </td>
                      <td className="border border-gray-200 p-2 dark:border-slate-700">{String(r.type ?? '—')}</td>
                      <td className="border border-gray-200 p-2 dark:border-slate-700">{String(r.username ?? '—') || '—'}</td>
                      <td className="border border-gray-200 p-2 dark:border-slate-700">{fmtTs(r.createdAt)}</td>
                      <td className="border border-gray-200 p-2 text-right dark:border-slate-700">{deviceCount}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      </div>
    </PortaledLayer>
  );
}
